import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Table,
  Badge,
  Button,
  Spinner,
  UncontrolledTooltip,
} from "reactstrap";
import { Eye, Download, FileText, CheckCircle, Clock, AlertTriangle, CreditCard } from "react-feather";
import axios from "axios";
import { downloadSubscriptionInvoice } from "../../../utility/downloadSubscriptionInvoice";

const API_URL = process.env.REACT_APP_BASEURL || "http://localhost:8000/api/";

const getToken = () => {
  return localStorage.getItem(process.env.REACT_APP_USERTOKEN);
};

const InvoiceHistory = ({ onBillingStateChange }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadNotice, setDownloadNotice] = useState(null);

  const handleDownload = async (invoice) => {
    setDownloadNotice(null);
    const downloaded = await downloadSubscriptionInvoice(invoice.id);
    if (!downloaded) {
      setDownloadNotice(
        "The PDF is temporarily unavailable. Please use View Online Invoice and try the download again shortly."
      );
    }
  };

  const fetchInvoiceHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = {
        Accept: "application/json",
        Authorization: `Bearer ${getToken()}`,
      };
      const [paymentsResponse, stripeResponse] = await Promise.all([
        axios.get(`${API_URL}brand/load-payment-history?per_page=100`, { headers }),
        axios.get(`${API_URL}brand/subscription-invoices`, { headers }),
      ]);

      const localPayments = paymentsResponse.data?.data ||
        (Array.isArray(paymentsResponse.data) ? paymentsResponse.data : []);
      const localByTransaction = new Map(
        localPayments
          .filter((payment) => payment.transaction_id)
          .map((payment) => [payment.transaction_id, payment])
      );
      const stripeInvoices = (stripeResponse.data?.invoices || []).map((invoice) => ({
          ...(localByTransaction.get(invoice.id) || {}),
          id: invoice.id,
          invoice_number: invoice.number,
          transaction_id: invoice.id,
          purchased_date: invoice.created_at,
          created_timestamp: invoice.created_timestamp,
          subscription_plan: invoice.plan_name,
          subscription_type: "invoice",
          paid_amount: invoice.is_paid ? invoice.amount_paid : invoice.amount,
          payment_status: invoice.status,
          stripe_status: invoice.stripe_status,
          paid_at: invoice.paid_at,
          period_start: invoice.period_start,
          period_end: invoice.period_end,
          subscription_status: invoice.subscription_status,
          discount: invoice.discount_amount > 0
            ? `$${Number(invoice.discount_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
            : (localByTransaction.get(invoice.id)?.discount || ""),
          hosted_invoice_url: invoice.hosted_invoice_url,
          download_url: invoice.invoice_pdf,
        }));

      const stripeIds = new Set(stripeInvoices.map((invoice) => invoice.transaction_id));
      const localOnly = localPayments.filter(
        (payment) => !payment.transaction_id || !stripeIds.has(payment.transaction_id)
      );

      setInvoices([...stripeInvoices, ...localOnly].sort((a, b) => {
        const aTime = a.created_timestamp || Date.parse(a.purchased_datetime || a.purchased_date) || 0;
        const bTime = b.created_timestamp || Date.parse(b.purchased_datetime || b.purchased_date) || 0;
        return bTime - aTime;
      }));
      if (onBillingStateChange) {
        onBillingStateChange(stripeResponse.data);
      }
    } catch (err) {
      setError("Failed to load invoice history.");
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoiceHistory();
  }, []);

  const getStatusBadge = (status) => {
    const s = (status || "paid").toLowerCase();
    if (s === "paid" || s === "succeeded") {
      return (
        <Badge color="light-success" pill className="d-inline-flex align-items-center">
          <CheckCircle size={12} className="mr-25" />
          Paid
        </Badge>
      );
    }
    if (s === "pending" || s === "open") {
      return (
        <Badge color="light-warning" pill className="d-inline-flex align-items-center">
          <Clock size={12} className="mr-25" />
          {s === "pending" ? "Pending" : "Unpaid"}
        </Badge>
      );
    }
    if (s === "void") {
      return <Badge color="light-secondary" pill>Void</Badge>;
    }
    if (s === "uncollectible") {
      return <Badge color="light-danger" pill>Uncollectible</Badge>;
    }
    if (s === "payment_failed" || s === "failed") {
      return (
        <Badge color="light-danger" pill className="d-inline-flex align-items-center">
          <AlertTriangle size={12} className="mr-25" /> Payment Failed
        </Badge>
      );
    }
    return (
      <Badge color="light-danger" pill className="d-inline-flex align-items-center">
        <AlertTriangle size={12} className="mr-25" />
        {status || "Unpaid"}
      </Badge>
    );
  };

  return (
    <Card className="card-user-timeline shadow-sm">
      <CardHeader className="d-flex justify-content-between align-items-center border-bottom pb-1">
        <div>
          <CardTitle tag="h4" className="mb-0">
            Invoice & Billing History
          </CardTitle>
          <small className="text-muted font-small-2">
            Real-time invoices, receipts, and payment transactions
          </small>
        </div>
        <Button
          size="sm"
          color="flat-primary"
          onClick={fetchInvoiceHistory}
          disabled={loading}
        >
          {loading ? <Spinner size="sm" /> : "Refresh"}
        </Button>
      </CardHeader>
      <CardBody className="p-0">
        {downloadNotice && (
          <div className="alert alert-warning rounded-0 mb-0 font-small-2" role="alert">
            {downloadNotice}
          </div>
        )}
        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="text-muted mt-1 font-small-3">Loading invoices...</p>
          </div>
        ) : error ? (
          <div className="text-center py-3 text-danger font-small-3">
            {error}
          </div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-4 text-muted">
            <FileText size={36} className="text-secondary mb-1" />
            <h6>No invoices found</h6>
            <p className="font-small-3 mb-0">
              When you purchase or renew a plan, your billing invoices will appear here.
            </p>
          </div>
        ) : (
          <Table responsive hover className="mb-0">
            <thead className="thead-light font-small-2">
              <tr>
                <th>INVOICE #</th>
                <th>CREATED / PAID</th>
                <th>PLAN / DESCRIPTION</th>
                <th>SUBSCRIPTION PERIOD</th>
                <th>AMOUNT</th>
                <th>STATUS</th>
                <th className="text-right">ACTIONS</th>
              </tr>
            </thead>
            <tbody className="font-small-3">
              {invoices.map((inv) => {
                const invoiceNumber =
                  inv.invoice_number ||
                  inv.transaction_id ||
                  `INV-${String(inv.id).padStart(5, "0")}`;
                const shortInvoiceNum =
                  invoiceNumber.length > 18
                    ? `${invoiceNumber.substring(0, 18)}...`
                    : invoiceNumber;
                const formattedAmount =
                  typeof inv.paid_amount === "number"
                    ? `$${inv.paid_amount.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`
                    : `$${inv.paid_amount || inv.total_amount || 0}`;

                const canDownloadPdf = Boolean(
                  inv.download_url && String(inv.id || "").startsWith("in_")
                );

                return (
                  <tr key={inv.id}>
                    <td>
                      <span
                        className="font-weight-bold text-primary cursor-pointer"
                        id={`inv-tooltip-${inv.id}`}
                        onClick={() => {
                          if (inv.hosted_invoice_url) {
                            window.open(inv.hosted_invoice_url, "_blank");
                          }
                        }}
                      >
                        {shortInvoiceNum}
                      </span>
                      {invoiceNumber.length > 18 && (
                        <UncontrolledTooltip target={`inv-tooltip-${inv.id}`}>
                          {invoiceNumber}
                        </UncontrolledTooltip>
                      )}
                    </td>
                    <td className="text-nowrap">
                      <div>{inv.purchased_date || "—"}</div>
                      {inv.paid_at && (
                        <small className="text-success d-block">Paid {inv.paid_at}</small>
                      )}
                    </td>
                    <td>
                      <div className="font-weight-bolder text-dark">
                        {inv.subscription_plan || "Brand Plan"}
                      </div>
                      {inv.discount && (
                        <small className="text-success font-small-2">
                          Discount: {inv.discount}
                        </small>
                      )}
                    </td>
                    <td>
                      {inv.period_start && inv.period_end ? (
                        <div className="font-small-2 text-nowrap">
                          <div>{inv.period_start}</div>
                          <div className="text-muted">to {inv.period_end}</div>
                          {inv.subscription_status === "scheduled" && (
                            <Badge color="light-primary" pill className="mt-25 font-small-1">
                              Scheduled
                            </Badge>
                          )}
                          {inv.subscription_status === "pending_payment" && (
                            <Badge color="light-warning" pill className="mt-25 font-small-1">
                              Starts after payment
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <Badge color="light-info" pill className="font-small-2 text-capitalize">
                          {inv.subscription_type || "Invoice"}
                        </Badge>
                      )}
                    </td>
                    <td className="font-weight-bolder text-dark text-nowrap">
                      {formattedAmount}
                    </td>
                    <td>{getStatusBadge(inv.payment_status)}</td>
                    <td className="text-right text-nowrap">
                      {inv.hosted_invoice_url && ["open", "payment_failed"].includes((inv.payment_status || "").toLowerCase()) ? (
                        <Button
                          size="sm"
                          color="primary"
                          className="mr-25 font-small-2"
                          onClick={() => window.open(inv.hosted_invoice_url, "_blank")}
                          title="Pay Invoice"
                        >
                          <CreditCard size={14} className="mr-25" /> Pay
                        </Button>
                      ) : inv.hosted_invoice_url ? (
                        <Button
                          size="sm"
                          color="flat-primary"
                          className="btn-icon mr-25"
                          onClick={() => window.open(inv.hosted_invoice_url, "_blank")}
                          title="View Online Invoice / Receipt"
                        >
                          <Eye size={16} />
                        </Button>
                      ) : null}

                      {canDownloadPdf ? (
                        <Button
                          size="sm"
                          color="flat-secondary"
                          className="btn-icon"
                          onClick={() => handleDownload(inv)}
                          title="Download Invoice PDF"
                        >
                          <Download size={16} />
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </CardBody>
    </Card>
  );
};

export default InvoiceHistory;
